import React, { useEffect, useState, useCallback } from "react";
import {
  Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Switch, FormControlLabel, Box, Tooltip, Typography,
  Table, TableHead, TableBody, TableRow, TableCell
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import VisibilityIcon from "@material-ui/icons/Visibility";
import AddIcon from "@material-ui/icons/Add";
import { toast } from "react-toastify";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";
import api from "../../services/api";
import useConfirm from "../../hooks/useConfirm";

const useStyles = makeStyles(theme => ({
  desc: { color: theme.palette.text.secondary, display: "block" },
  chips: { display: "flex", gap: 4, flexWrap: "wrap" },
  muted: { color: theme.palette.text.hint },
  num: { fontVariantNumeric: "tabular-nums" },
  json: { width: "100%", fontFamily: "monospace", fontSize: 12 },
  // #f7f7f7 fixo virava texto claro sobre fundo claro no tema escuro.
  pre: {
    background: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    fontSize: 12,
    maxHeight: 240,
    overflow: "auto",
  }
}));

const emptyForm = {
  name: "",
  description: "",
  trigger: "",
  active: true,
  priority: 100,
  conditions: [],
  actions: []
};

const Automations = () => {
  const classes = useStyles();
  const [confirm, confirmDialog] = useConfirm();
  const [meta, setMeta] = useState({ triggers: [], actions: [], operators: [] });
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ q: "", trigger: "", active: "" });
  const [dlg, setDlg] = useState({ open: false, editing: null, form: emptyForm });
  const [previewDlg, setPreviewDlg] = useState({ open: false, payloadRaw: "{}", result: null, automationId: null });

  const loadMeta = useCallback(async () => {
    try {
      const { data } = await api.get("/automations/meta");
      setMeta(data || { triggers: [], actions: [], operators: [] });
    } catch (err) { /* silent */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filter.q) params.q = filter.q;
      if (filter.trigger) params.trigger = filter.trigger;
      if (filter.active !== "") params.active = filter.active;
      const { data } = await api.get("/automations", { params });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) { toast.error("Erro ao listar automações"); }
  }, [filter]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => setDlg({ open: true, editing: null, form: { ...emptyForm } });
  const openEdit = (a) => setDlg({
    open: true, editing: a.id,
    form: {
      name: a.name || "",
      description: a.description || "",
      trigger: a.trigger || "",
      active: !!a.active,
      priority: a.priority ?? 100,
      conditions: Array.isArray(a.conditions) ? a.conditions : [],
      actions: Array.isArray(a.actions) ? a.actions : []
    }
  });

  const save = async () => {
    try {
      const f = dlg.form;
      if (!f.name || !f.trigger) { toast.error("Nome e gatilho são obrigatórios"); return; }
      const payload = {
        name: f.name,
        description: f.description || null,
        trigger: f.trigger,
        active: !!f.active,
        priority: Number(f.priority || 100),
        conditions: f.conditions || [],
        actions: f.actions || []
      };
      if (dlg.editing) await api.put(`/automations/${dlg.editing}`, payload);
      else await api.post("/automations", payload);
      toast.success("Automação salva");
      setDlg({ open: false, editing: null, form: emptyForm });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erro ao salvar automação");
    }
  };

  const remove = async (id) => {
    if (!(await confirm({ title: "Remover esta automação?", message: "Ela para de disparar imediatamente. Os logs de execução são mantidos." }))) return;
    try {
      await api.delete(`/automations/${id}`);
      toast.success("Automação removida");
      load();
    } catch (err) { toast.error("Erro ao remover"); }
  };

  const runNow = async (id) => {
    const raw = window.prompt("Payload JSON para execução manual:", "{}");
    if (raw === null) return;
    try {
      const payload = JSON.parse(raw || "{}");
      const { data } = await api.post(`/automations/${id}/run`, { payload });
      toast.success(data?.success ? "Executada" : "Falhou (ver log)");
    } catch (err) { toast.error("Erro ao executar"); }
  };

  const openPreview = (a) => setPreviewDlg({ open: true, payloadRaw: "{}", result: null, automationId: a.id });
  const runPreview = async () => {
    try {
      const payload = JSON.parse(previewDlg.payloadRaw || "{}");
      const { data } = await api.post(`/automations/${previewDlg.automationId}/preview`, { payload });
      setPreviewDlg(p => ({ ...p, result: data }));
    } catch (err) { toast.error("Payload JSON inválido ou erro"); }
  };

  // condition row helpers
  const addCondition = () => setDlg(d => ({
    ...d, form: { ...d.form, conditions: [...(d.form.conditions || []), { path: "", op: "eq", value: "" }] }
  }));
  const updateCondition = (i, patch) => setDlg(d => {
    const arr = [...(d.form.conditions || [])];
    arr[i] = { ...arr[i], ...patch };
    return { ...d, form: { ...d.form, conditions: arr } };
  });
  const removeCondition = (i) => setDlg(d => {
    const arr = [...(d.form.conditions || [])];
    arr.splice(i, 1);
    return { ...d, form: { ...d.form, conditions: arr } };
  });

  // action row helpers
  const addAction = () => setDlg(d => ({
    ...d, form: { ...d.form, actions: [...(d.form.actions || []), { type: meta.actions[0] || "audit_log", params: {} }] }
  }));
  const updateAction = (i, patch) => setDlg(d => {
    const arr = [...(d.form.actions || [])];
    arr[i] = { ...arr[i], ...patch };
    return { ...d, form: { ...d.form, actions: arr } };
  });
  const updateActionParams = (i, raw) => setDlg(d => {
    const arr = [...(d.form.actions || [])];
    let parsed = {};
    try { parsed = raw ? JSON.parse(raw) : {}; } catch (e) { /* keep */ }
    arr[i] = { ...arr[i], params: parsed, _raw: raw };
    return { ...d, form: { ...d.form, actions: arr } };
  });
  const removeAction = (i) => setDlg(d => {
    const arr = [...(d.form.actions || [])];
    arr.splice(i, 1);
    return { ...d, form: { ...d.form, actions: arr } };
  });

  const columns = [
    {
      key: "name",
      label: "Nome",
      render: a => (
        <div>
          <Typography variant="subtitle2">{a.name}</Typography>
          {a.description && (
            <Typography variant="caption" className={classes.desc}>{a.description}</Typography>
          )}
        </div>
      ),
    },
    { key: "trigger", label: "Gatilho", render: a => <StatusChip tone="info" label={a.trigger} /> },
    {
      key: "actions_list",
      label: "Ações",
      render: a => {
        const acts = a.actions || [];
        if (!acts.length) return <span className={classes.muted}>nenhuma</span>;
        return (
          <div className={classes.chips}>
            {acts.map((ac, i) => <StatusChip key={i} tone="neutral" label={ac.type} />)}
          </div>
        );
      },
    },
    { key: "priority", label: "Prioridade", align: "center", render: a => <span className={classes.num}>{a.priority}</span> },
    { key: "runCount", label: "Execuções", align: "center", render: a => <span className={classes.num}>{a.runCount || 0}</span> },
    {
      key: "active",
      label: "Situação",
      align: "center",
      render: a => <StatusChip dot tone={a.active ? "success" : "neutral"} label={a.active ? "Ativa" : "Inativa"} />,
    },
    {
      key: "actions",
      label: "Operações",
      type: "actions",
      render: a => (
        <>
          <Tooltip title="Testar com um payload de exemplo">
            <IconButton size="small" onClick={() => openPreview(a)}><VisibilityIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Executar agora">
            <IconButton size="small" onClick={() => runNow(a.id)}><PlayArrowIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => openEdit(a)}><EditIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Remover">
            <IconButton size="small" onClick={() => remove(a.id)}><DeleteIcon fontSize="small" /></IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <MainContainer>
      {confirmDialog}
      <PageHeader
        title="Automações"
        count={items.length}
        subtitle="Regras que reagem a um gatilho do CRM e executam ações sem ninguém apertar nada."
        actions={
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openCreate}>
            Nova automação
          </Button>
        }
        filters={
          <>
            <TextField label="Buscar" value={filter.q}
              onChange={(e) => setFilter(f => ({ ...f, q: e.target.value }))} />
            <TextField label="Gatilho" select style={{ minWidth: 180 }} value={filter.trigger}
              onChange={(e) => setFilter(f => ({ ...f, trigger: e.target.value }))}>
              <MenuItem value="">Todos</MenuItem>
              {meta.triggers.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField label="Status" select style={{ minWidth: 140 }} value={filter.active}
              onChange={(e) => setFilter(f => ({ ...f, active: e.target.value }))}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="true">Ativas</MenuItem>
              <MenuItem value="false">Inativas</MenuItem>
            </TextField>
          </>
        }
      />

      <DataTable
        columns={columns}
        rows={items}
        emptyIcon={<FlashOnIcon />}
        emptyTitle="Nenhuma automação cadastrada"
        emptyDescription="Automações reagem a eventos do CRM — negócio ganho, ticket sem resposta, conta vencida — e executam ações sozinhas."
        emptyAction={
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openCreate}>
            Nova automação
          </Button>
        }
      />

      {/* Edit dialog */}
      <Dialog open={dlg.open} onClose={() => setDlg({ open: false, editing: null, form: emptyForm })} fullWidth maxWidth="md">
        <DialogTitle>{dlg.editing ? "Editar automação" : "Nova automação"}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" gap={1} style={{ gap: 8 }}>
            <TextField fullWidth label="Nome" value={dlg.form.name}
              onChange={(e) => setDlg(d => ({ ...d, form: { ...d.form, name: e.target.value } }))} />
            <TextField select fullWidth label="Gatilho" value={dlg.form.trigger}
              onChange={(e) => setDlg(d => ({ ...d, form: { ...d.form, trigger: e.target.value } }))}>
              {meta.triggers.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField type="number" label="Prioridade" value={dlg.form.priority}
              onChange={(e) => setDlg(d => ({ ...d, form: { ...d.form, priority: e.target.value } }))} />
          </Box>
          <Box mt={2}>
            <TextField fullWidth multiline rows={2} label="Descrição" value={dlg.form.description}
              onChange={(e) => setDlg(d => ({ ...d, form: { ...d.form, description: e.target.value } }))} />
          </Box>
          <FormControlLabel
            style={{ marginTop: 8 }}
            control={<Switch checked={!!dlg.form.active}
              onChange={(e) => setDlg(d => ({ ...d, form: { ...d.form, active: e.target.checked } }))} />}
            label="Ativa"
          />

          <Box mt={2}>
            <strong>Condições (todas devem casar)</strong>
            <Button size="small" onClick={addCondition} style={{ marginLeft: 8 }}>+ adicionar</Button>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Caminho</TableCell>
                  <TableCell>Operador</TableCell>
                  <TableCell>Valor</TableCell>
                  <TableCell>Valor 2 (between)</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {(dlg.form.conditions || []).map((c, i) => (
                  <TableRow key={i}>
                    <TableCell><TextField size="small" value={c.path || ""} onChange={(e) => updateCondition(i, { path: e.target.value })} /></TableCell>
                    <TableCell>
                      <TextField size="small" select style={{ minWidth: 110 }} value={c.op || "eq"}
                        onChange={(e) => updateCondition(i, { op: e.target.value })}>
                        {(meta.operators || []).map(op => <MenuItem key={op} value={op}>{op}</MenuItem>)}
                      </TextField>
                    </TableCell>
                    <TableCell><TextField size="small" value={c.value ?? ""} onChange={(e) => updateCondition(i, { value: e.target.value })} /></TableCell>
                    <TableCell><TextField size="small" value={c.value2 ?? ""} onChange={(e) => updateCondition(i, { value2: e.target.value })} /></TableCell>
                    <TableCell><IconButton size="small" onClick={() => removeCondition(i)}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          <Box mt={2}>
            <strong>Ações</strong>
            <Button size="small" onClick={addAction} style={{ marginLeft: 8 }}>+ adicionar</Button>
            {(dlg.form.actions || []).map((a, i) => (
              <Box key={i} mt={1} p={1} border={1} borderColor="#eee" borderRadius={4}>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <TextField size="small" select style={{ minWidth: 180 }} label="Tipo" value={a.type}
                    onChange={(e) => updateAction(i, { type: e.target.value })}>
                    {(meta.actions || []).map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </TextField>
                  <Box flex={1} />
                  <IconButton size="small" onClick={() => removeAction(i)}><DeleteIcon fontSize="small" /></IconButton>
                </Box>
                <TextField
                  className={classes.json}
                  multiline minRows={3}
                  margin="dense"
                  label="Parâmetros (JSON)"
                  value={a._raw !== undefined ? a._raw : JSON.stringify(a.params || {}, null, 2)}
                  onChange={(e) => updateActionParams(i, e.target.value)}
                />
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg({ open: false, editing: null, form: emptyForm })}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={save}>Salvar</Button>
        </DialogActions>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={previewDlg.open} onClose={() => setPreviewDlg({ open: false, payloadRaw: "{}", result: null, automationId: null })} fullWidth maxWidth="sm">
        <DialogTitle>Preview de automação</DialogTitle>
        <DialogContent dividers>
          <TextField fullWidth multiline minRows={4} label="Payload JSON" className={classes.json}
            value={previewDlg.payloadRaw}
            onChange={(e) => setPreviewDlg(p => ({ ...p, payloadRaw: e.target.value }))} />
          {previewDlg.result && (
            <Box mt={2}>
              <div><strong>Resultado:</strong></div>
              <div>Casou condições?{" "}
                <StatusChip dot tone={previewDlg.result.matched ? "success" : "neutral"}
                  label={previewDlg.result.matched ? "Sim" : "Não"} />
              </div>
              <pre className={classes.pre}>
                {JSON.stringify(previewDlg.result, null, 2)}
              </pre>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDlg({ open: false, payloadRaw: "{}", result: null, automationId: null })}>Fechar</Button>
          <Button color="primary" variant="contained" onClick={runPreview}>Simular</Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Automations;
